import { possessive } from '@shared';
import {
  Citations,
  AnimationToggle,
  OnboardingLayout,
  FunctionalStateAnimation,
  useSlideSetup,
  useAnimationPreferences,
  citations,
} from '@/features/client-onboarding';

import { useSelector } from 'react-redux';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

import {
  selectClient,
  selectInferenceSet,
  selectIntakeSurvey,
  selectAppliedInferenceSet,
} from '@/model';
import { isInferenceEdited } from '@/util';
import {
  OnboardingOurPredictions,
  ColumnLtcLikelihoodEver,
  ColumnLtcAtAge,
  ColumnLtcAgeFallBack,
} from '@/pages/OnboardingOurPredictions';

function getDecade(age: number): number {
  return Math.floor(age / 10) * 10;
}

export function GenworthOnboardingOurPredictions(_props: ClientIdProps) {
  const { flags } = useFeatureFlags();
  const client = useSelector(selectClient);

  const {
    clientFirstName,
    appliedInferenceSet: { ltcAtAgeShowRange, ltcAtAge },
  } = client;

  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { ltcLikelihoodEver } = useSelector(selectInferenceSet);
  const { animationsEnabled } = useAnimationPreferences();

  const showAnimations = flags.animatedSlides && animationsEnabled;

  useSlideSetup({
    slidePage: OnboardingOurPredictions,
    nextButtonLabel: `${possessive(clientFirstName)} Care Journey`,
  });

  const phaseCopy = [
    {
      title: 'Currently',
      label: 'Usually independent and fully capable',
      description: `Here's the typical care journey we see for people like ${clientFirstName}.`,
    },
    {
      title: `In their ${getDecade(ltcAtAge - 10)}s`,
      label: 'Often start to slow down but stay capable',
      description: `Most people like ${clientFirstName} begin to need some assistance as they slow down.`,
    },
    {
      title: 'In need of LTC help',
      label: 'Often unable to perform daily activities alone',
      description: `About ${Math.round(ltcLikelihoodEver * 100)}% of people like ${clientFirstName} submit a long-term care claim, and it usually starts around age ${ltcAtAge}.`,
    },
  ];

  return (
    <OnboardingLayout>
      <div className="flex h-max flex-col items-center justify-center pb-4">
        <div className="w-full py-2 md:py-8">
          <div className="mb-6 flex w-full grow items-center justify-between gap-6">
            <h1 className="text-balance text-2xl font-bold text-gray-950 md:text-4xl">
              A Typical Care Journey for People Like {clientFirstName}
            </h1>
            {flags.animatedSlides && (
              <div className="flex items-center gap-2">
                <AnimationToggle />
              </div>
            )}
          </div>
          {showAnimations ? (
            <FunctionalStateAnimation
              client={client}
              ltcLikelihood={ltcLikelihoodEver}
              ltcAtAge={ltcAtAge}
              intakeSurvey={intakeSurvey}
              ltcAtAgeIsEdited={isInferenceEdited(client, 'ltcAtAge')}
              phaseCopy={phaseCopy}
              likelihoodLabel="Submit an LTC claim"
              ltcAtAgeLabel="Age a claim usually starts"
            />
          ) : (
            <div className="flex w-full grow flex-col gap-4 sm:flex-row">
              <ColumnLtcLikelihoodEver />
              <ColumnLtcAtAge />
              <ColumnLtcAgeFallBack />
            </div>
          )}
        </div>
        <div className="mt-auto w-full pt-4">
          <Citations
            citations={
              ltcAtAgeShowRange
                ? [
                    citations.onboardingOurPredictionsLikelihoodNationalAverage,
                    citations.onboardingOurPredictionsLtcAtAgeRange,
                  ]
                : [citations.onboardingOurPredictionsLikelihoodNationalAverage]
            }
            className="w-full rounded-lg"
          />
        </div>
      </div>
    </OnboardingLayout>
  );
}
